/**
 * Sentry initialization for apps/web.
 *
 * Mirrors the pattern used by apps/pelipost-os/src/main.tsx and
 * apps/pelipost-pro — same org, separate project (`pelipost-web`).
 *
 * Design decisions:
 *   - PII off by default (`sendDefaultPii: false`). We opt in to capturing
 *     only the internal user UUID via `setSentryUser()` (no email, no name,
 *     no phone) — per SC-20260417-001 decision captured in thread.
 *   - `enabled: !!VITE_SENTRY_DSN` — Sentry stays dark in local dev unless
 *     a DSN is explicitly provided. No noise from developers' machines.
 *   - `tracesSampleRate: 0` — performance tracing off for v1 to keep the
 *     Sentry bill bounded; flip on later if we need it.
 *   - `release: VITE_COMMIT_HASH` — exactly matches the release name the
 *     vite plugin uses when uploading source maps, so stack traces resolve
 *     to our original source.
 */

import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_COMMIT_HASH as string | undefined,
    environment: import.meta.env.MODE,
    enabled: Boolean(dsn),
    enableLogs: true,
    sendDefaultPii: false,
    integrations: [Sentry.browserApiErrorsIntegration()],
    tracesSampleRate: 0,
  });
}

/**
 * Associate subsequent error reports with the signed-in user by their internal
 * UUID only. Per SC-20260417-001 we intentionally do NOT send email, name, or
 * phone — the UUID alone lets us answer "is this bug hitting one user or
 * many?" without widening the PII surface.
 */
export function setSentryUser(userId: string | null): void {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
